/**
 * StickyBookBar — appears after the hero scrolls out of view.
 * Mobile: fixed bottom bar. Desktop: fixed top bar (below navbar).
 * Used on all high-revenue treatment pages.
 */
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { CalendarDays, Phone, MessageSquare } from "lucide-react";
import { trackBookingClick, trackPhoneClick } from "@/lib/gtm";
import { BOOKING_URL } from "@/lib/booking";
import { getRouteTreatmentBookingConfig } from "@shared/bookingLinks";

const PHONE = "tel:2142476793";
const SMS = "sms:2142476793";

interface StickyBookBarProps {
  treatmentName: string;
  /** Price hint e.g. "From $350/session" */
  priceHint?: string;
  /** Scroll threshold in px before bar appears. Default 400. */
  threshold?: number;
}

export default function StickyBookBar({
  treatmentName,
  priceHint,
  threshold = 400,
}: StickyBookBarProps) {
  const [visible, setVisible] = useState(false);
  const [location] = useLocation();
  const routeBooking = getRouteTreatmentBookingConfig(location);
  const primaryHref = routeBooking?.primaryHref ?? BOOKING_URL;
  const primaryLabel = routeBooking ? `Book ${routeBooking.shortName}` : "Book Now";

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return (
    <>
      {/* ── Mobile: bottom bar ── */}
      <div
        className="mobile-sticky-bar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: "rgba(139,107,107,0.97)",
          backdropFilter: "blur(8px)",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          padding: "0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom))",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          transform: visible ? "translateY(0)" : "translateY(110%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <a
          href={PHONE}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            flexShrink: 0,
          }}
        aria-label="Call us"
          onClick={() => trackPhoneClick("sticky_bar_call")}
        >
          <Phone size={18} />
        </a>
        <a
          href={SMS}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            flexShrink: 0,
          }}
          aria-label="Text us"
        >
          <MessageSquare size={18} />
        </a>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "rgba(255,255,255,0.9)", fontFamily: "'Lato',sans-serif", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            {treatmentName}
          </div>
          {priceHint && (
            <div style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Lato',sans-serif", fontSize: "0.7rem" }}>
              {priceHint}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <a
            href={primaryHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackBookingClick("sticky_bar_mobile_specific")}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: "#5B7A9E", color: "#fff", fontFamily: "'Lato',sans-serif",
              fontSize: routeBooking ? "0.65rem" : "0.78rem", fontWeight: 700,
              letterSpacing: "0.06em", textTransform: "uppercase", padding: "0.7rem 0.75rem",
              minHeight: 40, borderRadius: 8, textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            <CalendarDays size={13} />
            {primaryLabel}
          </a>
        </div>
      </div>

      {/* ── Desktop: top bar (below sticky navbar at ~68px) ── */}
      <div
        className="desktop-sticky-bar"
        style={{
          position: "fixed",
          top: 68,
          left: 0,
          right: 0,
          zIndex: 49,
          background: "rgba(139,107,107,0.96)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          padding: "0.55rem 0",
          transform: visible ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <div
          className="container"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ color: "rgba(255,255,255,0.9)", fontFamily: "'Lato',sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              {treatmentName}
            </span>
            {priceHint && (
              <span style={{ color: "rgba(255,255,255,0.55)", fontFamily: "'Lato',sans-serif", fontSize: "0.72rem" }}>
                {priceHint}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <a
              href={PHONE}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                color: "rgba(255,255,255,0.75)", fontFamily: "'Lato',sans-serif",
                fontSize: "0.72rem", fontWeight: 600, textDecoration: "none",
              }}
            >
              <Phone size={13} /> (214) 247-6793
            </a>
            <a
              href={primaryHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookingClick("sticky_bar_desktop_specific")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#5B7A9E", color: "#fff",
                fontFamily: "'Lato',sans-serif", fontSize: "0.7rem",
                fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                padding: "0.5rem 0.85rem", borderRadius: 6, textDecoration: "none", whiteSpace: "nowrap",
              }}
            >
              <CalendarDays size={13} />
              {primaryLabel}
            </a>
          </div>
        </div>
      </div>

      {/* CSS to show/hide per breakpoint */}
      <style>{`
        @media (min-width: 768px) { .mobile-sticky-bar { display: none !important; } }
        @media (max-width: 767px) { .desktop-sticky-bar { display: none !important; } }
      `}</style>
    </>
  );
}
